import { ProjectAvatar } from '@/shared/components/custom/avatars/ProjectAvatar';
import { PALETTE } from '@/core/lib/colors';
import { useProject } from '@/features/projects/public';
import type {
  BoardView,
  BoardViewInfo,
  GroupByOption,
  TaskListViewConfig,
  TaskFilters,
} from '../../types';
import { ActionToolbar } from './ActionToolbar';
import { ViewTabsBar } from './ViewTabsBar';
import { MemberBar } from './MemberBar';

function resolveProjectColor(color: string | null, name: string): string {
  if (color) return color;
  const hash = name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
  return PALETTE[hash % PALETTE.length].token;
}

interface TaskHeaderProps {
  projectId: string;
  boardId: string;
  activeView: BoardView;
  onViewChange: (view: BoardView) => void;
  config: TaskListViewConfig;
  filters: TaskFilters;
  onGroupByChange: (value: GroupByOption) => void;
  onToggleCompleted: (show: boolean) => void;
  onToggleArchived?: (show: boolean) => void;
  onFiltersChange: (filters: TaskFilters) => void;
  boardName?: string;
  boardViews?: BoardViewInfo[];
  boardViewsLoading?: boolean;
  onRemoveView?: (viewId: string) => void;
  addViewControl?: React.ReactNode;
  /** View-specific toolbar controls (e.g. Timeline's "Hoy" + "Lapso") — see `ActionToolbar`. */
  viewControls?: React.ReactNode;
}

export function TaskHeader({
  projectId,
  boardId,
  activeView,
  onViewChange,
  config,
  filters,
  onGroupByChange,
  onToggleCompleted,
  onToggleArchived,
  onFiltersChange,
  boardName = 'Main Board',
  boardViews = [],
  boardViewsLoading = false,
  onRemoveView,
  addViewControl,
  viewControls,
}: TaskHeaderProps) {
  const { data: project } = useProject(projectId);

  const projectName = project?.name ?? projectId;
  const projectColor = resolveProjectColor(project?.color ?? null, projectName);

  return (
    <div data-testid="tasks-header-root" className="flex flex-col">
      {/* Row 1 — top bar */}
      <div className="mb-2 flex items-center gap-2 py-2">
        <nav
          data-testid="tasks-header-breadcrumb"
          className="flex min-w-0 items-center gap-2"
          aria-label="breadcrumb"
        >
          <ProjectAvatar name={projectName} color={projectColor} size="sm" />
          <span className="truncate text-sm font-medium">{projectName}</span>
          <span className="text-muted-foreground" aria-hidden>
            /
          </span>
          <span className="hidden truncate text-sm font-medium md:inline">{boardName}</span>
        </nav>

        <MemberBar projectId={projectId} />
      </div>

      {/* Row 2 — view tabs */}
      <div className="mb-4">
        <ViewTabsBar
          views={boardViews}
          activeView={activeView}
          onViewChange={onViewChange}
          onRemoveView={onRemoveView ?? (() => undefined)}
          isLoading={boardViewsLoading}
          addViewControl={addViewControl}
        />
      </div>

      {/* Row 3 — action toolbar (list, board, calendar and timeline views) */}
      {(activeView === 'list' ||
        activeView === 'board' ||
        activeView === 'calendar' ||
        activeView === 'timeline') && (
        <ActionToolbar
          projectId={projectId}
          boardId={boardId}
          config={config}
          filters={filters}
          viewMode={activeView}
          onGroupByChange={onGroupByChange}
          onToggleCompleted={onToggleCompleted}
          onToggleArchived={onToggleArchived}
          onFiltersChange={onFiltersChange}
          viewControls={viewControls}
        />
      )}
    </div>
  );
}
